import { __awaiter } from '../../node_modules/tslib/tslib.es6.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { Actions, Action } from '../../ai-elements/actions.js';
import { Shimmer } from '../../ai-elements/shimmer.js';
import brandclub_colors from '../../colors/brandclubColors.js';
import '../../icons/abstract/AbstractArcIcon.js';
import '../../icons/AddCircleIcon.js';
import '../../icons/AddCircleOutlineIcon.js';
import '../../icons/ArrowCircleRightIcon.js';
import '../../icons/ArrowLeftIcon.js';
import '../../icons/ArrowRightIcon.js';
import '../../icons/BoltIcon.js';
import '../../icons/BookmarkStarIcon.js';
import '../../icons/CalendarIcon.js';
import '../../icons/CancelIcon.js';
import '../../icons/CheckCircleIcon.js';
import '../../icons/CheckMarkIcon.js';
import '../../icons/ChevronDownIcon.js';
import '../../icons/ChevronLeftIcon.js';
import '../../icons/ChevronRightIcon.js';
import '../../icons/ChevronUpIcon.js';
import '../../icons/CloseIcon.js';
import { CopyIcon } from '../../icons/CopyIcon.js';
import '../../icons/MenuIcon.js';
import '../../icons/NotificationActiveIcon.js';
import '../../icons/PaperPlaneIcon.js';
import '../../icons/PendingIcon.js';
import '../../icons/PersonIcon.js';
import '../../icons/PlayCircleIcon.js';
import '../../icons/RemoveCircleIcon.js';
import '../../icons/SearchIcon.js';
import '../../icons/SellIcon.js';
import { ShareIcon } from '../../icons/ShareIcon.js';
import '../../icons/ShoppingBagIcon.js';
import '../../icons/SmartphoneIcon.js';
import '../../icons/StarIcon.js';
import '../../icons/StarOutlineIcon.js';
import '../../icons/SuccessCheckCircleIcon.js';
import { ThumbsDownIcon } from '../../icons/ThumbsDownIcon.js';
import { ThumbsUpIcon } from '../../icons/ThumbsUpIcon.js';
import '../../icons/TuneIcon.js';
import '../../icons/FourPointStarIcon.js';
import React__default, { useState, useMemo, useRef, useLayoutEffect } from 'react';
import { toast } from 'sonner';
import { getHeadersWithUniqueVisitorId } from '../../Auth/index.js';
import { setConversationSharing, sendMessageFeedback } from '../api.js';
import { renderMessageContentWithCarousels } from '../utils/messageRenderer.js';
import { ProductSearchQueriesSection } from './ProductSearchQueriesSection.js';
import { StoreLocatorWidget } from './StoreLocatorWidget/index.js';
import { OrderCardList } from './OrderCard.js';
import { ChainOfThoughtWidget, MessageBubbleWidget, customColor, themeColor, FollowUpQueriesWidget } from '@brandclub/bc-widgets';

const ChatMessageComponent = React__default.forwardRef(({ role, content, bottomRef, isTyping = false, isStreaming = false, isLastMessage = false, messageIndex, conversationId, feedback, followUpQueries = [], nextUserMessage, productMetadataMap, surveyMetadataMap, streamingProductGroups, currentStatus, previewSearchQueries = [], previewQueryProducts = [], featuredProduct, streamingFeaturedProduct, storeLocator, streamingStoreLocator, orders, imageUrl, imageData, imageMimeType, onSearch, onUpdateFeedback, onVariantSelect, onSuggestionClick, apiBaseUrl, }) => {
    var _a;
    const isUser = role === 'user';
    // Initialize feedback state from props
    const [liked, setLiked] = useState((feedback === null || feedback === void 0 ? void 0 : feedback.type) === 'like');
    const [disliked, setDisliked] = useState((feedback === null || feedback === void 0 ? void 0 : feedback.type) === 'dislike');
    // Track locally selected follow-up query (for immediate UI feedback before message is sent)
    const [localSelectedFollowUp, setLocalSelectedFollowUp] = useState(undefined);
    // Derive selected follow-up from next user message (if it matches one of the follow-up queries)
    const derivedSelectedFollowUp = useMemo(() => {
        if (!nextUserMessage || !followUpQueries.length)
            return undefined;
        // Check if any follow-up query matches the next user message
        return followUpQueries.find((query) => query === nextUserMessage);
    }, [nextUserMessage, followUpQueries]);
    // Use derived selection if available, otherwise use local selection
    const selectedFollowUp = derivedSelectedFollowUp || localSelectedFollowUp;
    // Ref and state for measuring if user message is multiline
    const userMessageRef = useRef(null);
    const [isMultiline, setIsMultiline] = useState(false);
    // Measure the user message to determine if it's multiline using getClientRects()
    // This is the most reliable method as it counts actual rendered line boxes
    useLayoutEffect(() => {
        if (!isUser || !userMessageRef.current)
            return;
        const el = userMessageRef.current;
        const textNode = el.firstChild;
        if (textNode && textNode.nodeType === Node.TEXT_NODE) {
            // For text nodes, use Range.getClientRects() to count lines
            const range = document.createRange();
            range.selectNodeContents(el);
            const rects = range.getClientRects();
            // Each rect represents a line box - more than 1 means multiline
            setIsMultiline(rects.length > 1);
        }
        else {
            // Fallback: compare scrollHeight to single line height
            const computedStyle = window.getComputedStyle(el);
            const lineHeight = parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.5;
            // If content height exceeds ~1.5 lines, it's multiline
            setIsMultiline(el.scrollHeight > lineHeight * 1.5);
        }
    }, [isUser, content]);
    const formatProductForCopy = (product, isFeatured) => {
        const parts = [`Title: ${product.title}`];
        if (product.brandName) {
            parts.push(`Brand: ${product.brandName}`);
        }
        if (product.retailPrice !== undefined) {
            parts.push(`Price: $${product.retailPrice.toFixed(2)}`);
        }
        // Construct Brandclub URL
        const url = `${window.location.origin}/product/p/${product.stacklineSku}/product`;
        parts.push(`URL: ${url}`);
        // Add feature bullets for featured products
        if (isFeatured && product.featureBullets && product.featureBullets.length > 0) {
            parts.push(`\nFeatures:`);
            product.featureBullets.forEach((bullet) => {
                parts.push(`• ${bullet}`);
            });
        }
        return parts.join('\n');
    };
    const convertProductTagsToText = (text) => {
        let result = text;
        // Replace featured product tags (with or without self-closing slash)
        // Handle multiple featured products by looking them up in productMetadataMap
        result = result.replace(/<product\s+featured="true"\s+stacklineSku="([^"]+)"\s*(?:question="[^"]+")?\s*\/?>/g, (match, stacklineSku) => {
            // First try to find in productMetadataMap (for multiple featured products)
            let product = productMetadataMap.get(stacklineSku);
            // Fall back to props for backward compatibility (single featured product)
            if (!product) {
                product = (featuredProduct === null || featuredProduct === void 0 ? void 0 : featuredProduct.product) || (streamingFeaturedProduct === null || streamingFeaturedProduct === void 0 ? void 0 : streamingFeaturedProduct.product);
                if (product && product.stacklineSku !== stacklineSku) {
                    product = undefined; // Clear if SKU doesn't match
                }
            }
            if (product) {
                return '\n\n' + formatProductForCopy(product, true) + '\n\n';
            }
            return match;
        });
        // Replace product group tags
        result = result.replace(/<product_group[^>]*>([\s\S]*?)<\/product_group>/g, (match, innerContent) => {
            // Extract product tags from within the group (with or without self-closing slash)
            const productTags = innerContent.match(/<product\s+stacklineSku="([^"]+)"\s*\/?>/g);
            if (!productTags)
                return match;
            const products = [];
            productTags.forEach((tag) => {
                const skuMatch = tag.match(/stacklineSku="([^"]+)"/);
                if (skuMatch) {
                    const sku = skuMatch[1];
                    const product = productMetadataMap.get(sku);
                    if (product) {
                        products.push(formatProductForCopy(product, false));
                    }
                }
            });
            return products.length > 0 ? '\n\n' + products.join('\n---\n') + '\n\n' : match;
        });
        // Replace citation tags with source URL
        result = result.replace(/<citation\s+[^>]*>([\s\S]*?)<\/citation>/g, (match, citationText) => {
            // Extract source attribute
            const sourceMatch = match.match(/source\s*=\s*"([^"]+)"/);
            const source = sourceMatch ? sourceMatch[1] : '';
            if (source) {
                return `[${citationText.trim()}](${source})`;
            }
            return citationText;
        });
        return result;
    };
    const handleCopy = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const textWithProducts = convertProductTagsToText(content);
            yield navigator.clipboard.writeText(textWithProducts);
            toast.success('Message copied to clipboard');
        }
        catch (error) {
            toast.error('Failed to copy message');
        }
    });
    const handleShare = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!conversationId) {
            console.warn('Cannot share: missing conversationId');
            return;
        }
        try {
            // Get headers with auth and visitor ID
            const headers = yield getHeadersWithUniqueVisitorId();
            // Enable sharing for the conversation
            const result = yield setConversationSharing({
                conversationId,
                enabled: true,
                headersToInject: headers,
                apiBaseUrl,
            });
            // Copy the share URL to clipboard
            yield navigator.clipboard.writeText(result.shareUrl);
            toast.success('Share link copied to clipboard');
        }
        catch (error) {
            console.error('Failed to share conversation:', error);
            toast.error('Failed to share conversation');
        }
    });
    const handleLike = () => __awaiter(void 0, void 0, void 0, function* () {
        var _b;
        if (!conversationId || messageIndex === undefined) {
            toast.error('Cannot like: conversation not loaded');
            return;
        }
        try {
            const newState = !liked;
            // Get headers with auth and visitor ID
            const headers = yield getHeadersWithUniqueVisitorId();
            // Call API to persist feedback FIRST
            const result = yield sendMessageFeedback({
                conversationId,
                messageIndex,
                feedbackType: newState ? 'like' : null,
                headersToInject: headers,
                apiBaseUrl,
            });
            // Only update UI state after successful API call
            setLiked(newState);
            setDisliked(false);
            toast.success(newState ? 'Message liked' : 'Like removed');
            // Update parent component state with new feedback
            if (onUpdateFeedback && ((_b = result.message.metadata) === null || _b === void 0 ? void 0 : _b.feedback)) {
                onUpdateFeedback(messageIndex, result.message.metadata.feedback);
            }
        }
        catch (error) {
            console.error('Failed to update feedback:', error);
            toast.error('Failed to save feedback');
        }
    });
    const handleDislike = () => __awaiter(void 0, void 0, void 0, function* () {
        var _c;
        if (!conversationId || messageIndex === undefined) {
            toast.error('Cannot dislike: conversation not loaded');
            return;
        }
        try {
            const newState = !disliked;
            // Get headers with auth and visitor ID
            const headers = yield getHeadersWithUniqueVisitorId();
            // Call API to persist feedback FIRST
            const result = yield sendMessageFeedback({
                conversationId,
                messageIndex,
                feedbackType: newState ? 'dislike' : null,
                headersToInject: headers,
                apiBaseUrl,
            });
            // Only update UI state after successful API call
            setDisliked(newState);
            setLiked(false);
            toast.success(newState ? 'Message disliked' : 'Dislike removed');
            // Update parent component state with new feedback
            if (onUpdateFeedback && ((_c = result.message.metadata) === null || _c === void 0 ? void 0 : _c.feedback)) {
                onUpdateFeedback(messageIndex, result.message.metadata.feedback);
            }
        }
        catch (error) {
            console.error('Failed to update feedback:', error);
            toast.error('Failed to save feedback');
        }
    });
    const handleFollowUpClick = (query) => {
        // Don't allow clicking if not the last message or already selected
        if (!isLastMessage || selectedFollowUp)
            return;
        // Set local selection immediately for responsive UI
        setLocalSelectedFollowUp(query);
        // Auto-send the query
        onSearch === null || onSearch === void 0 ? void 0 : onSearch(query);
    };
    // Helper to get flex justify-content value from alignment
    const getJustifyContent = (alignment) => {
        switch (alignment) {
            case 'right':
                return 'flex-end';
            case 'center':
                return 'center';
            default:
                return 'flex-start';
        }
    };
    // Render user message bubble with widget styling
    const renderUserMessageBubble = () => (jsx(MessageBubbleWidget, Object.assign({ widgetId: "concierge-user-message", displayName: "User Message Bubble", initialState: {
            bgColor: customColor(brandclub_colors.blueWhite),
            textColor: themeColor('textPrimary'),
            maxWidth: '70%',
            alignment: 'right',
        } }, { children: (styles, resolveColor) => {
            // Use measured isMultiline state for reliable multiline detection
            const borderRadius = isMultiline ? styles.borderRadius : styles.borderRadiusShort;
            // Determine font-family: use fontFamilyCss from widget if available, otherwise fallback to CSS variable
            const fontFamily = styles.fontFamilyCss
                ? styles.fontFamilyCss !== 'inherit'
                    ? styles.fontFamilyCss
                    : 'var(--concierge-user-message-font-family, inherit)'
                : 'var(--concierge-user-message-font-family, inherit)';
            return (jsx("div", Object.assign({ style: {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: getJustifyContent(styles.alignment),
                    width: '100%',
                } }, { children: jsx("div", Object.assign({ style: { maxWidth: styles.maxWidth } }, { children: jsx("div", Object.assign({ className: "message-content user-message", style: {
                            maxWidth: '100%',
                            minWidth: '50px',
                            backgroundColor: resolveColor(styles.bgColor),
                            color: resolveColor(styles.textColor),
                            borderRadius: `${borderRadius}px`,
                            wordWrap: 'break-word',
                            padding: `${styles.paddingY}px ${styles.paddingX}px`,
                            fontSize: `${styles.fontSize}px`,
                            fontWeight: styles.fontWeight,
                            fontFamily: fontFamily,
                            lineHeight: styles.lineHeight,
                        } }, { children: jsx("div", Object.assign({ ref: userMessageRef, style: { whiteSpace: 'pre-wrap' } }, { children: content })) })) })) })));
        } })));
    // Render assistant message bubble with widget styling
    const renderAssistantMessageBubble = () => (jsx(MessageBubbleWidget, Object.assign({ widgetId: "concierge-assistant-message", displayName: "Assistant Message Bubble", initialState: {
            bgColor: themeColor('secondary'),
            textColor: themeColor('textPrimary'),
            maxWidth: '100%',
            paddingX: 0,
            paddingY: 0,
            alignment: 'left',
        } }, { children: (styles, resolveColor) => (jsx("div", Object.assign({ style: {
                display: 'flex',
                flexDirection: 'column',
                alignItems: getJustifyContent(styles.alignment),
                width: '100%',
            } }, { children: jsxs("div", Object.assign({ style: { maxWidth: styles.maxWidth } }, { children: [jsxs("div", Object.assign({ className: "message-content", style: {
                            maxWidth: '100%',
                            minWidth: '50px',
                            backgroundColor: resolveColor(styles.bgColor),
                            color: resolveColor(styles.textColor),
                            borderRadius: `${styles.borderRadius}px`,
                            wordWrap: 'break-word',
                            padding: `${styles.paddingY}px ${styles.paddingX}px`,
                            fontSize: `${styles.fontSize}px`,
                            fontWeight: styles.fontWeight,
                            lineHeight: styles.lineHeight,
                        } }, { children: [renderMessageContentWithCarousels(content, productMetadataMap, streamingProductGroups, isStreaming, isStreaming, // streaming messages are already processed
                            onVariantSelect, surveyMetadataMap), isStreaming && content.length > 0 && (jsx("span", { style: {
                                    display: 'inline-block',
                                    width: '2px',
                                    height: '1em',
                                    backgroundColor: 'currentColor',
                                    animation: 'blink 1s infinite',
                                    marginLeft: '2px',
                                } })), orders && orders.length > 0 && jsx(OrderCardList, { orders: orders })] })), bottomRef && (jsx("div", { style: {
                            height: '1px',
                            width: '100%',
                        }, ref: bottomRef })), !isTyping && !isStreaming && content.length > 0 ? (jsxs(Actions, Object.assign({ className: "tw:mt-2" }, { children: [jsx(Action, Object.assign({ label: "Copy", tooltip: "Copy", onClick: handleCopy }, { children: jsx(CopyIcon, {}) })), jsx(Action, Object.assign({ label: "Like", tooltip: "Like", onClick: handleLike }, { children: jsx(ThumbsUpIcon, { className: liked ? 'tw:fill-current tw:text-primary' : '' }) })), jsx(Action, Object.assign({ label: "Dislike", tooltip: "Dislike", onClick: handleDislike }, { children: jsx(ThumbsDownIcon, { className: disliked ? 'tw:fill-current tw:text-primary' : '' }) })), jsx(Action, Object.assign({ label: "Share", tooltip: "Share", onClick: handleShare }, { children: jsx(ShareIcon, {}) }))] }))) : null, !isTyping && !isStreaming && followUpQueries.length > 0 && !(nextUserMessage && !selectedFollowUp) && (jsx(FollowUpQueriesWidget, Object.assign({ widgetId: "concierge-follow-up-queries", displayName: "Follow-up Suggestions" }, { children: (widgetStyles, resolveColor) => {
                            // Determine if pills should be disabled (not last message or already selected)
                            const isDisabled = !isLastMessage || !!selectedFollowUp;
                            return (jsx("div", Object.assign({ style: {
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: `${widgetStyles.containerGap}px`,
                                    marginTop: '16px',
                                    width: '100%',
                                } }, { children: followUpQueries.map((query, index) => {
                                    const isSelected = selectedFollowUp === query;
                                    // Determine styling based on state - resolve theme colors
                                    let bgColor = resolveColor(widgetStyles.pillBgColor);
                                    let textColor = resolveColor(widgetStyles.pillTextColor);
                                    let borderColor = resolveColor(widgetStyles.pillBorderColor);
                                    let cursor = 'pointer';
                                    let opacity = 1;
                                    if (isSelected) {
                                        // Selected state: dark background, light text
                                        bgColor = resolveColor(widgetStyles.pillBgColorSelected);
                                        textColor = resolveColor(widgetStyles.pillTextColorSelected);
                                        borderColor = resolveColor(widgetStyles.pillBorderColorSelected);
                                        cursor = 'default';
                                    }
                                    else if (isDisabled) {
                                        // Disabled state: greyed out
                                        opacity = 0.5;
                                        cursor = 'not-allowed';
                                    }
                                    return (jsx("button", Object.assign({ onClick: () => handleFollowUpClick(query), disabled: isDisabled && !isSelected, style: {
                                            padding: `${widgetStyles.pillPaddingY}px ${widgetStyles.pillPaddingX}px`,
                                            borderRadius: `${widgetStyles.pillBorderRadius}px`,
                                            border: `${widgetStyles.pillBorderWidth}px solid ${borderColor}`,
                                            backgroundColor: bgColor,
                                            color: textColor,
                                            fontSize: `${widgetStyles.pillFontSize}px`,
                                            fontWeight: widgetStyles.pillFontWeight,
                                            cursor,
                                            opacity,
                                            transition: 'all 0.2s ease',
                                            lineHeight: '22px',
                                            maxWidth: '100%',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }, onMouseEnter: (e) => {
                                            if (!isDisabled && !isSelected) {
                                                e.currentTarget.style.backgroundColor = resolveColor(widgetStyles.pillBgColorHover);
                                            }
                                        }, onMouseLeave: (e) => {
                                            if (!isDisabled && !isSelected) {
                                                e.currentTarget.style.backgroundColor = resolveColor(widgetStyles.pillBgColor);
                                            }
                                        }, title: query }, { children: query }), index));
                                }) })));
                        } })))] })) }))) })));
    return (jsxs("div", Object.assign({ className: `message ${role}`, style: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
        } }, { children: [!isUser && (previewSearchQueries.length > 0 || previewQueryProducts.length > 0) && (jsx(ProductSearchQueriesSection, { previewSearchQueries: previewSearchQueries, previewQueryProducts: previewQueryProducts, isStreaming: isStreaming, currentStatus: currentStatus, hasContent: content.length > 0, isLastMessage: isLastMessage })), !isUser && (storeLocator || streamingStoreLocator) && (jsx(StoreLocatorWidget, { stacklineSku: ((_a = (storeLocator || streamingStoreLocator)) === null || _a === void 0 ? void 0 : _a.stacklineSku) || '', apiUrl: apiBaseUrl })), !isUser && isTyping && (jsx(ChainOfThoughtWidget, Object.assign({ widgetId: "chain-of-thought", displayName: "Chain of Thought" }, { children: (styles, resolveColor) => styles.thinkingEnabled ? (jsx("div", Object.assign({ style: { maxWidth: '100%' } }, { children: jsx("div", Object.assign({ className: "message-content", style: {
                            maxWidth: '100%',
                            backgroundColor: resolveColor(styles.thinkingBgColor),
                            color: resolveColor(styles.thinkingTextColor),
                            borderRadius: `${styles.thinkingBorderRadius}px`,
                            borderBottomLeftRadius: `${styles.thinkingBorderRadiusBottomLeft}px`,
                            borderBottomRightRadius: `${styles.thinkingBorderRadius}px`,
                            wordWrap: 'break-word',
                            padding: `${styles.thinkingPaddingY}px ${styles.thinkingPaddingX}px`,
                        } }, { children: jsx(Shimmer, Object.assign({ as: "p", style: { fontSize: `${styles.thinkingTextFontSize}px` }, duration: styles.shimmerDuration }, { children: styles.thinkingText })) })) }))) : null }))), isUser && (imageUrl || imageData) && (jsx("div", Object.assign({ style: {
                    display: 'flex',
                    justifyContent: 'flex-end',
                    width: '100%',
                    marginBottom: '0.5rem',
                } }, { children: jsx("div", Object.assign({ style: { maxWidth: '70%' } }, { children: jsx("img", { src: imageUrl || `data:${imageMimeType};base64,${imageData}`, alt: "User uploaded image", style: {
                            width: '100%',
                            maxHeight: '400px',
                            objectFit: 'cover',
                            display: 'block',
                            borderRadius: '12px',
                        } }) })) }))), !isTyping && (isUser ? renderUserMessageBubble() : renderAssistantMessageBubble())] })));
});
// Memoize the component to prevent unnecessary re-renders
const ChatMessage = React__default.memo(ChatMessageComponent, (prevProps, nextProps) => {
    var _a, _b, _c, _d, _e, _f;
    // Custom comparison function to prevent re-renders when props haven't meaningfully changed
    return (prevProps.role === nextProps.role &&
        prevProps.content === nextProps.content &&
        prevProps.isTyping === nextProps.isTyping &&
        prevProps.bottomRef === nextProps.bottomRef &&
        prevProps.isStreaming === nextProps.isStreaming &&
        prevProps.isLastMessage === nextProps.isLastMessage &&
        prevProps.currentStatus === nextProps.currentStatus &&
        prevProps.isWaitingForFollowUpQueries === nextProps.isWaitingForFollowUpQueries &&
        ((_a = prevProps.previewSearchQueries) === null || _a === void 0 ? void 0 : _a.length) === ((_b = nextProps.previewSearchQueries) === null || _b === void 0 ? void 0 : _b.length) &&
        ((_c = prevProps.previewQueryProducts) === null || _c === void 0 ? void 0 : _c.length) === ((_d = nextProps.previewQueryProducts) === null || _d === void 0 ? void 0 : _d.length) &&
        ((_e = prevProps.followUpQueries) === null || _e === void 0 ? void 0 : _e.length) === ((_f = nextProps.followUpQueries) === null || _f === void 0 ? void 0 : _f.length) &&
        prevProps.nextUserMessage === nextProps.nextUserMessage &&
        prevProps.productMetadataMap === nextProps.productMetadataMap &&
        prevProps.streamingProductGroups === nextProps.streamingProductGroups &&
        prevProps.featuredProduct === nextProps.featuredProduct &&
        prevProps.streamingFeaturedProduct === nextProps.streamingFeaturedProduct &&
        prevProps.storeLocator === nextProps.storeLocator &&
        prevProps.streamingStoreLocator === nextProps.streamingStoreLocator &&
        prevProps.orders === nextProps.orders &&
        prevProps.imageUrl === nextProps.imageUrl &&
        prevProps.imageData === nextProps.imageData &&
        prevProps.imageMimeType === nextProps.imageMimeType &&
        prevProps.onSearch === nextProps.onSearch &&
        prevProps.onVariantSelect === nextProps.onVariantSelect &&
        prevProps.onSuggestionClick === nextProps.onSuggestionClick);
});

export { ChatMessage };
//# sourceMappingURL=ChatMessage.js.map
